// src/components/AddCompanyForm/RegionsCard.jsx
import React, { useState, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
  Switch,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { slugify, uid } from "./utils";

export default function RegionsCard({ regioes, setRegioes }) {
  const [newRegionLabel, setNewRegionLabel] = useState("");

  const patchRegion = (id, patch) => {
    setRegioes((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  };

  const addRegion = () => {
    const clean = (newRegionLabel || "").trim();
    if (!clean) return;
    const key = slugify(clean);
    if (!key) return alert("Nome de marca inválido.");
    if (regioes.some((r) => r.region_key === key)) return alert("Essa marca já existe.");
    setRegioes((prev) => [
      ...prev,
      {
        _id: uid(),
        region_key: key,
        label: clean,
        ordem: prev.length,
        status: 1,
        restrita: 0,
        has_region_access_password: false,
        nova_senha_acesso: "",
      },
    ]);
    setNewRegionLabel("");
  };
  const removeRegion = (id) => setRegioes((p) => p.filter((r) => r._id !== id));
  const onRegionKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addRegion();
    }
  };

  return (
    <Card variant="outlined">
      <CardHeader title="Regiões do projeto" subheader="projetos_regioes" />
      <CardContent sx={{ pt: 0 }}>
        <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Nova marca — nome/label"
              value={newRegionLabel}
              onChange={(e) => setNewRegionLabel(e.target.value)}
              onKeyDown={onRegionKeyDown}
              fullWidth
              placeholder="Workspace da marca"
              helperText="Deve ser exatamente igual ao workspace da marca"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button variant="outlined" onClick={addRegion} fullWidth>
              Adicionar
            </Button>
          </Grid>
        </Grid>

        {regioes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma marca definida.
          </Typography>
        ) : (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Marque &quot;Restrita&quot; para exigir senha no e-commerce ao resolver o CEP para essa região. A senha é
              armazenada com hash; deixe em branco ao salvar para manter a senha atual (se já existir).
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Marca</TableCell>
                  <TableCell align="center">Restrita</TableCell>
                  <TableCell>Nova senha</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {regioes.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {r.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.region_key}
                        {r.has_region_access_password ? " · senha configurada" : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={Number(r.restrita) === 1}
                            onChange={(e) =>
                              patchRegion(r._id, {
                                restrita: e.target.checked ? 1 : 0,
                                ...(e.target.checked ? {} : { nova_senha_acesso: "" }),
                              })
                            }
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        type="password"
                        autoComplete="new-password"
                        disabled={Number(r.restrita) !== 1}
                        placeholder={Number(r.restrita) === 1 ? "Nova senha (opcional)" : "—"}
                        value={r.nova_senha_acesso || ""}
                        onChange={(e) => patchRegion(r._id, { nova_senha_acesso: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" aria-label="Remover região" onClick={() => removeRegion(r._id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export const useRegionKeys = (regioes) => useMemo(() => regioes.map((r) => r.region_key), [regioes]);
